/* Compõe uma notificação de admin-push (ADR-0687). Texto LIVRE (título/corpo) + severidade + público
   (todos / usuários selecionados por id) + rota opcional + expiração. Cria em DRAFT; a publicação é
   separada (botão na lista). Regra de ouro: DialogContent locked enquanto salva. */
import { createUserNotificationBody } from '@planuze/admin-contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InlineAlert,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@planuze/ui';
import { useState } from 'react';
import { useNotificationsAdminContent } from '../../content/sections/notifications-admin.js';
import { useCreateAdminNotification } from '../../hooks/use-admin-notifications.js';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
}

type Severity = 'info' | 'success' | 'warning' | 'error';
type Audience = 'all' | 'users';

export function NotificationComposeModal({ open, onClose }: Props) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState<Severity>('info');
  const [audience, setAudience] = useState<Audience>('all');
  const [userIdsRaw, setUserIdsRaw] = useState('');
  const [action, setAction] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useCreateAdminNotification();
  const saving = create.isPending;
  const copy = useNotificationsAdminContent().composeModal;

  const submit = (): void => {
    setError(null);
    const userIds = userIdsRaw
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const parsed = createUserNotificationBody.safeParse({
      title,
      severity,
      audience,
      ...(body.trim().length > 0 && { body }),
      ...(audience === 'users' && { userIds }),
      ...(action.trim().length > 0 && { action }),
      ...(expiresAt.length > 0 && { expiresAt: new Date(expiresAt).toISOString() }),
    });
    if (!parsed.success) {
      setError(copy.validationError);
      return;
    }
    create.mutate(parsed.data, {
      onSuccess: () => {
        toast.success(copy.createdToast);
        onClose();
        setTitle('');
        setBody('');
        setUserIdsRaw('');
        setAction('');
        setExpiresAt('');
      },
      onError: () => setError(copy.createErrorToast),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent locked={saving}>
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {error !== null && <InlineAlert variant="destructive">{error}</InlineAlert>}
          <div>
            <Label htmlFor="notif-title">{copy.titleLabel}</Label>
            <Input id="notif-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="notif-body">{copy.bodyLabel}</Label>
            <Textarea
              id="notif-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <Label>{copy.severityLabel}</Label>
              <Select value={severity} onValueChange={(v) => setSeverity(v as Severity)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">{copy.severityInfo}</SelectItem>
                  <SelectItem value="success">{copy.severitySuccess}</SelectItem>
                  <SelectItem value="warning">{copy.severityWarning}</SelectItem>
                  <SelectItem value="error">{copy.severityError}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label>{copy.audienceLabel}</Label>
              <Select value={audience} onValueChange={(v) => setAudience(v as Audience)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{copy.audienceAll}</SelectItem>
                  <SelectItem value="users">{copy.audienceUsers}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {audience === 'users' && (
            <div>
              <Label htmlFor="notif-users">{copy.userIdsLabel}</Label>
              <Textarea
                id="notif-users"
                value={userIdsRaw}
                onChange={(e) => setUserIdsRaw(e.target.value)}
                rows={2}
              />
            </div>
          )}
          <div className="flex gap-3">
            <div className="flex-1">
              <Label htmlFor="notif-action">{copy.actionLabel}</Label>
              <Input
                id="notif-action"
                value={action}
                placeholder="/actions"
                onChange={(e) => setAction(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <Label htmlFor="notif-expires">{copy.expiresLabel}</Label>
              <Input
                id="notif-expires"
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {copy.cancel}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {copy.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
